import { DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { deleteDraft, listDrafts } from '../../data/drafts';
import { describeApiError } from '../../data/http';
import type { DraftSummary } from '../../data/types';

export function DraftsPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    return listDrafts()
      .then((items) => {
        setDrafts(items);
        setError(null);
      })
      .catch((err: unknown) => setError(describeApiError(err, t('errors.loadDrafts'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (draft: DraftSummary) => {
    const confirmed = await showConfirmModal({
      title: t('drafts.deleteTitle'),
      description: t('drafts.deleteDescription', { name: draft.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteDraft(draft.draftId);
      setDrafts((current) => current.filter((d) => d.draftId !== draft.draftId));
      toast({ title: t('drafts.deleted'), type: 'success' });
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.deleteDraft')), type: 'error' });
    }
  };

  return (
    <>
      <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">{t('drafts.title')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('drafts.subtitle')}</p>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error && drafts.length === 0 ? (
        <section className="mt-6 rounded-lg border border-border bg-surface p-6">
          <div className="grid size-10 place-items-center rounded bg-accent text-accent-foreground">
            <DocumentTextIcon className="size-5" aria-hidden="true" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-foreground">{t('drafts.emptyTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('drafts.emptyBody')}</p>
        </section>
      ) : null}

      {!loading && !error && drafts.length > 0 ? (
        <ul className="mt-6 grid gap-3">
          {drafts.map((draft) => (
            <li key={draft.draftId} className="rounded-lg border border-border bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/app/new/${draft.templateId}/${draft.step}?draft=${encodeURIComponent(draft.draftId)}`}
                    className="font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    {draft.name}
                  </Link>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {draft.templateName} · {t('drafts.updated', { when: new Date(draft.modifiedAtUtc).toLocaleString(i18n.language) })}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => void remove(draft)}
                  aria-label={t('drafts.deleteLabel', { name: draft.name })}
                  className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-destructive transition hover:bg-destructive-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <TrashIcon className="size-4" aria-hidden="true" />
                  {t('common.delete')}
                </button>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div
                  role="progressbar"
                  aria-valuenow={draft.progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('drafts.progressLabel', { name: draft.name })}
                  className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
                >
                  <div className="h-full rounded-full bg-primary" data-progress={Math.round(draft.progressPercent / 5) * 5} />
                </div>
                <span className="text-xs text-muted-foreground">{t('drafts.progress', { percent: draft.progressPercent })}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
